import {
  Icon,
  IconButton,
  NavigationButton,
  Table,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { Timer } from 'lib-shared-legacy-components';
import { ampli } from 'lib-shared-tracking';
import { useNavigate } from 'react-router-dom';

import { DashboardQueue } from '../../../api/types';
import {
  QueuesOverviewColumnType,
  VISIBLE_QUEUES_OVERVIEW_COLUMNS,
} from '../shared';

import { Agents, Conversations, Name } from './components';
import styles from './queuesItem.module.scss';

interface QueuesItemProps {
  dashboardQueue: DashboardQueue;
  isFavourite: boolean;
  toggleFavouriteStatus: () => void;
}

const DEFAULT_TIME_LABEL_ELEMENT = <span className={styles.dim}>00:00:00</span>;
const ADD_FAVOURITE_QUEUE_LABEL = 'Add queue to my favorites';
const REMOVE_FAVOURITE_QUEUE_LABEL = 'Remove queue from my favorites';

export const QueuesItem = (props: QueuesItemProps) => {
  const navigate = useNavigate();

  const { dashboardQueue, isFavourite, toggleFavouriteStatus } = props;
  const {
    average_handling_time,
    average_waiting_time,
    breached_thresholds,
    closed_conversations,
    longest_wait,
    queue,
    service_level,
  } = dashboardQueue;
  const { id } = queue;

  const availableColumns: Record<QueuesOverviewColumnType, JSX.Element | null> =
    {
      [QueuesOverviewColumnType.Agents]: (
        <Agents dashboardQueue={dashboardQueue} />
      ),

      [QueuesOverviewColumnType.AverageHandlingTime]: (
        <div className={styles.bigText}>
          {average_handling_time ? (
            <Timer asCounter startAt={average_handling_time} start={false} />
          ) : (
            DEFAULT_TIME_LABEL_ELEMENT
          )}
        </div>
      ),

      [QueuesOverviewColumnType.AverageWaitTime]: (
        <div className={styles.bigText}>
          {average_waiting_time ? (
            <Timer asCounter startAt={average_waiting_time} start={false} />
          ) : (
            DEFAULT_TIME_LABEL_ELEMENT
          )}
        </div>
      ),

      [QueuesOverviewColumnType.Conversations]: (
        <Conversations dashboardQueue={dashboardQueue} />
      ),

      [QueuesOverviewColumnType.CurrentLongestWaitingTime]: (
        <div className={styles.bigText}>
          {longest_wait ? (
            <Timestamp counter={true} refreshRate={1000} time={longest_wait} />
          ) : (
            DEFAULT_TIME_LABEL_ELEMENT
          )}
        </div>
      ),

      // NOTE: While in favourites view, toggling the favourite will not remove
      // the item from the list as it's not good UX, as a click might have been
      // accidental which would result in losing the item.
      [QueuesOverviewColumnType.Favourite]: (
        <Tooltip
          content={
            isFavourite
              ? REMOVE_FAVOURITE_QUEUE_LABEL
              : ADD_FAVOURITE_QUEUE_LABEL
          }
        >
          <IconButton
            variant="tertiary"
            icon={isFavourite ? 'star-full' : 'star'}
            iconProps={{
              color: isFavourite ? colors.orange[300] : colors.neutral[400],
            }}
            onClick={toggleFavouriteStatus}
          />
        </Tooltip>
      ),

      [QueuesOverviewColumnType.HandledConversations]: (
        <span className={styles.bigText}>{closed_conversations || 0}</span>
      ),

      [QueuesOverviewColumnType.Name]: <Name dashboardQueue={dashboardQueue} />,

      [QueuesOverviewColumnType.ServiceLevelAgreement]: service_level ? (
        <span className={styles.bold}>{`${service_level.toFixed(1)}%`}</span>
      ) : null,

      [QueuesOverviewColumnType.TvDashboard]: (
        <NavigationButton
          navigate={() => navigate(`/tv/dashboard/${id}`)}
          onClick={() => ampli.dashboardQueueItemClicked()}
          className={styles.tvDashboardButton}
        >
          <Icon name="external-link" />
        </NavigationButton>
      ),
    };

  const rowClassNames = classnames({
    [styles.thresholdWarning]: !!Object.keys(breached_thresholds).length,
  });

  return (
    <Table.Tr className={rowClassNames}>
      {VISIBLE_QUEUES_OVERVIEW_COLUMNS.map((column) => {
        const { type, style } = column;
        const classNames = classnames({ [styles[type]]: true }, styles.row);

        return (
          <Table.Td key={type} style={style} className={classNames}>
            {availableColumns[type]}
          </Table.Td>
        );
      })}
    </Table.Tr>
  );
};
